/**
 * Exercises API — typed migration of ExercisesAPI.js
 */
import apiClient from './client';
import type { Exercise, Worksheet } from './types';

const ExercisesAPI = {
  /** Get all exercises for current user */
  async getUserExercises(): Promise<Exercise[]> {
    const { data } = await apiClient.get<any>('/v3/all-exercises');
    const list = (data as any)?.result ?? data ?? [];
    return Array.isArray(list) ? list : [];
  },

  /** Get publicly accessible exercises (student browse) */
  async getPublicExercises(): Promise<Exercise[]> {
    try {
      const { data } = await apiClient.get<any>('/v3/all-exercises', { params: { access: 'public' } });
      const list = (data as any)?.result ?? data ?? [];
      const all = Array.isArray(list) ? list : [];
      // Client-side filter: show public or shared
      return all.filter((e: any) => e.access === 'public' || e.access === 'shared' || !e.access);
    } catch {
      return [];
    }
  },

  /** Get exercises belonging to a specific user (e.g. a teacher) */
  async getByUser(userId: string): Promise<Exercise[]> {
    try {
      const { data } = await apiClient.get<any>(`/v3/user-exercises/${userId}`);
      const list = (data as any)?.result ?? data ?? [];
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  },

  /** Get all exercises for a material */
  async getMaterialExercises(materialId: string): Promise<Exercise[]> {
    const { data } = await apiClient.get<Exercise[]>(
      '/v3/exercises',
      { params: { materialId } }
    );
    return data ?? [];
  },

  /** Get a single exercise by ID (with cards) */
  async getExercise(id: string): Promise<Exercise> {
    try {
      const { data } = await apiClient.get<any>(
        `/v3/exercise/${id}`,
        { validateStatus: status => (status >= 200 && status < 300) || status === 502 || status === 504 || status === 404 }
      );
      const res = (data as any)?.result ?? data;
      return res ?? {};
    } catch {
      return {} as Exercise;
    }
  },


  /** Create a new exercise */
  async createExercise(payload: Partial<Exercise>): Promise<Exercise> {
    const { data } = await apiClient.post<Exercise>(
      '/v3/exercises',
      payload
    );
    return data;
  },

  /** Create a card for an exercise */
  async createExerciseCard(payload: any): Promise<any> {
    const { data } = await apiClient.post('/v3/exercises-cards', payload);
    return data;
  },

  /** Update an exercise */
  async updateExercise(payload: Partial<Exercise> & { id: string }): Promise<Exercise> {
    const { data } = await apiClient.put<Exercise>(
      '/v3/exercises',
      payload
    );
    return data;
  },

  /** Delete an exercise (moves to trash) */
  async deleteExercise(id: string): Promise<void> {
    await apiClient.delete(`/v3/exercises/${id}`);
  },

  /** Get exercises in trash (deleted within last 7 days) */
  async getTrash(): Promise<Exercise[]> {
    const { data } = await apiClient.get<Exercise[]>('/v3/exercises/trash');
    return data ?? [];
  },

  /** Restore an exercise from trash */
  async restore(id: string): Promise<void> {
    await apiClient.post(`/v3/exercises/${id}/restore`, {});
  },

  /** Permanently delete an exercise from trash */
  async permanentDelete(id: string): Promise<void> {
    await apiClient.delete(`/v3/exercises/${id}/permanent`);
  },

  /** Clone an exercise — creates a copy with '(копия)' appended to the name */
  async cloneExercise(id: string): Promise<Exercise> {
    const original = await this.getExercise(id);
    const { id: _id, createdAt: _ca, updatedAt: _ua, ...rest } = original as any;
    return this.createExercise({ ...rest, name: `${original.name ?? ''} (копия)` });
  },

  /** Get worksheets for a student/class/exercise */
  async getWorksheets(params: {
    studentId?: string;
    classId?: string;
    exerciseId?: string;
  }): Promise<Worksheet[]> {
    const { data } = await apiClient.get<Worksheet[]>(
      '/v3/worksheets',
      { params }
    );
    return data ?? [];
  },

  /** Submit or save answers to a worksheet */
  async saveWorksheet(payload: Partial<Worksheet>): Promise<Worksheet> {
    const { data } = await apiClient.post<Worksheet>(
      '/v3/worksheets',
      payload
    );
    return data;
  },
};

export default ExercisesAPI;
